import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { IUpcomingSwapCallsReportResponse } from '@nkr/store/index';
import { ISwapCourier, IUpcomingSwapCalls } from '@nkr/models/index';
import { GridWrapper, HeaderWrapper } from './styles';
import { AgGridReact, AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import {
    IHeaderParams, GridApi, ColumnApi, GridReadyEvent,
    CsvExportParams, ValueGetterParams, ProcessCellForExportParams, FirstDataRenderedEvent
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { FormControl, Button } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    courierVendor: number,
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getUpcomingSwapCallsReport: (courierVendor: number) => Promise<IUpcomingSwapCallsReportResponse>,
}

interface IState {
    callList: IUpcomingSwapCalls[],
    swapCourierList: ISwapCourier[],
    swapCourierFilter: number
}

class CourierSwapCallsScheduleReportGrid extends React.Component<IProps, IState> {
    readonly state: IState = {
        callList: [],
        swapCourierList: [],
        swapCourierFilter: 0
    }

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.gridColumnApi = e.columnApi;

        e.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getUpcomingSwapCallsReport(this.props.courierVendor)
                .then((response: IUpcomingSwapCallsReportResponse) => {
                    let { callList, swapCouriers: swapCourierList } = response.data.results;

                    this.setState({
                        callList,
                        swapCourierList
                    });

                    this.props.hideLoading();
                }).catch((ex) => {
                    alert('An error occured while loading report data');
                    this.props.hideLoading();
                });
        });
    }

    dateRangeSearch = () => {
        this.getData();
    }

    onGridSizeChanged = () => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = this.gridColumnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        this.gridColumnApi.setColumnsVisible(columnsToShow, true);
        this.gridColumnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        this.gridApi.sizeColumnsToFit();
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'upcoming-swap-calls.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'Rank')
                    return params.node.rowIndex + 1;
                else if (colDef.colId == 'xpDate')
                    return moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        this.gridApi.exportDataAsCsv(params);
    }

    handleFilterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            swapCourierFilter: parseInt(e.currentTarget.value)
        });
    }

    render = () => {
        const reportColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Row #
                    </SortableHeaderComponent>,
                headerName: 'Row #',
                width: 55,
                cellClass: 'center-list-cell-left',
                headerClass: 'center-list-header',
                // resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
            },
            {
                colId: 'callInfo',
                field: 'callInfo',
                headerName: 'Date/Time',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Date/Time
                    </SortableHeaderComponent>,

                width: 200,
                sortable: true,
                comparator: Utility.dateComparator
                // sort: 'asc',
                // resizable: true
            },
            {
                colId: 'callType',
                field: 'callType',
                headerName: 'Call Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Call Type
                    </SortableHeaderComponent>,
                width: 160,
                sortable: true,
                // sort: 'asc',
                // resizable: true
            },
            {
                colId: 'swapManager',
                field: 'swapManager',
                headerName: 'Swap Manager',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Swap Manager
                    </SortableHeaderComponent>,
                width: 200,
                sortable: true,
                // sort: 'asc',
                // resizable: true
            },
            {
                colId: 'swapInfo',
                field: 'swapInfo',
                headerName: 'Swap Info',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 200,
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Swap Info
                    </SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                // resizable: true
            },
            {
                colId: 'swapSize',
                field: 'swapSize',
                headerName: 'Swap Size',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Swap Size
                    </SortableHeaderComponent>,
                width: 120,
                sortable: true,
                // sort: 'asc',
                // resizable: true
            },
            {
                colId: 'clusterNote',
                field: 'clusterNote',
                headerName: 'Swap Comments',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Swap Comments
                    </SortableHeaderComponent>,
                minWidth: 500,
                // maxWidth: 520,
                sortable: true,
                // sort: 'asc',
                // resizable: true,
                hide: this.props.courierVendor > 0
            },
            {
                colId: 'dialInInfo',
                field: 'dialInInfo',
                headerName: 'Dial-in Info',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Dial-In Info
                    </SortableHeaderComponent>,
                width: 220,
                sortable: true,
                // sort: 'asc',
                // resizable: true
            },
            {
                colId: 'leaderCode',
                field: 'leaderCode',
                headerName: 'Leader Code',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Leader Code
                    </SortableHeaderComponent>,
                width: 190,
                sortable: true,
                // sort: 'asc',
                // resizable: true,
                hide: this.props.courierVendor > 0
            },
            {
                colId: 'swapCourierName',
                field: 'swapCourierName',
                headerName: 'Swap Courier',
                cellClass: 'center-list-cell-right',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Swap Courier
                    </SortableHeaderComponent>,
                width: 190,
                sortable: true,
                // sort: 'asc',
                // resizable: true,
                hide: this.props.courierVendor > 0
            }
        ];

        const filteredList = this.state.callList.filter(x => {
            if (this.state.swapCourierFilter > 0) {
                return x.swapCourierId == this.state.swapCourierFilter
            }

            return true;
        });

        return (
            <>
                <HeaderWrapper>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontSize: 28, fontWeight: 500 }}>
                            Upcoming Swap Calls
                        </div>

                        <div>
                            {this.props.courierVendor == 0 && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Filter by Swap Courier:</div>
                                    <div style={{ marginRight: 20 }}>
                                        <FormControl
                                            componentClass='select'
                                            value={this.state.swapCourierFilter}
                                            onChange={this.handleFilterChange}
                                        >
                                            <option value={0}>- All Couriers -</option>
                                            {this.state.swapCourierList.map(item =>
                                                <option key={`sc-${item.id}`} value={item.id}>{item.name}</option>
                                            )}
                                        </FormControl>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <Button bsStyle="primary" onClick={this.handleExport}>Export CSV</Button>
                        </div>
                    </div>
                </HeaderWrapper>

                <div className='row'>
                    <div className='col-sm-12'>
                        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
                            <AgGridReact
                                enableCellTextSelection={true}
                                enableRangeSelection={true}
                                columnDefs={reportColumns}
                                rowData={filteredList}
                                headerHeight={25}
                                noRowsOverlayComponentFramework={() =>
                                    <div>No data for the selected date range...</div>}
                                onGridReady={this.onGridReady}
                                onGridSizeChanged={this.onGridSizeChanged}
                                onFirstDataRendered={this.onFirstDataRendered}
                                suppressCellSelection={true}
                                onSortChanged={() => {
                                    this.gridApi.refreshCells();
                                }}
                            />
                        </GridWrapper>

                    </div>
                </div>
            </>
        );
    }
}

export default CourierSwapCallsScheduleReportGrid;